import React, { useMemo, useState } from 'react';
import { Table, Button, Input, Select, Space, Typography, DatePicker, Tag } from 'antd';
import { PlusOutlined, RightOutlined } from '@ant-design/icons';
import { CreateCustomerAssetData } from '../../services/customers';
import dayjs from 'dayjs';

const { Title } = Typography;

interface AssetsTabProps {
  isEdit: boolean;
  isEditing?: boolean;
  existingAssets: CreateCustomerAssetData[];
  planningAssets: CreateCustomerAssetData[];
  changeRequests?: Array<{
    field_name: string;
    status: string;
    old_value?: string | null;
    new_value?: string | null;
    id: number;
  }>;
  deletedAssets?: Array<{ category: 'planning' | 'existing'; index: number; data: any }>;
  addExistingAsset: () => void;
  removeExistingAsset: (index: number) => void;
  updateExistingAsset: (index: number, field: keyof CreateCustomerAssetData, value: string | number | undefined) => void;
  addPlanningAsset: () => void;
  removePlanningAsset: (index: number) => void;
  updatePlanningAsset: (index: number, field: keyof CreateCustomerAssetData, value: string | number | undefined) => void;
  getAssetDisplayValue: (category: 'planning' | 'existing', index: number, field: 'plate_no' | 'model' | 'address' | 'year' | 'price' | 'installment' | 'type') => string;
  handleAssetFieldChange: (category: 'planning' | 'existing', index: number, field: 'plate_no' | 'model' | 'address' | 'year' | 'price' | 'installment' | 'type', value: string) => void;
  handleApproveAssetChange: (category: 'planning' | 'existing', index: number, field: 'plate_no' | 'model' | 'address' | 'year' | 'price' | 'installment') => void;
  handleRejectAssetChange: (category: 'planning' | 'existing', index: number, field: 'plate_no' | 'model' | 'address' | 'year' | 'price' | 'installment') => void;
  isCustomerPortal?: boolean;
  handleApproveAssetRequests?: (ids: number[]) => void;
  handleRejectAssetRequests?: (ids: number[]) => void;
  handleApproveAssetDelete?: (requestId: number) => void;
  handleRejectAssetDelete?: (requestId: number) => void;
  handleApproveChangeRequest?: (requestId: number) => void;
  handleRejectChangeRequest?: (requestId: number) => void;
}

export const AssetsTab: React.FC<AssetsTabProps> = ({
  isEdit,
  isEditing = false,
  existingAssets,
  planningAssets,
  addExistingAsset,
  removeExistingAsset,
  updateExistingAsset,
  addPlanningAsset,
  removePlanningAsset,
  updatePlanningAsset,
  getAssetDisplayValue,
  handleAssetFieldChange,
  handleApproveAssetChange,
  handleRejectAssetChange,
  isCustomerPortal = false,
  changeRequests = [],
  handleApproveAssetRequests,
  handleRejectAssetRequests,
  deletedAssets = [],
  handleApproveAssetDelete,
  handleRejectAssetDelete,
  handleApproveChangeRequest,
  handleRejectChangeRequest,
}) => {
  const maxAssets = 10;
  // Category selector for the single section
  const [category, setCategory] = useState<'existing' | 'planning'>('existing');

  // Calculate totals for existing assets
  const existingTotals = useMemo(() => {
    const totalInstallment = existingAssets.reduce((sum, asset) => {
      const installment = typeof asset.installment === 'number' ? asset.installment : parseFloat(asset.installment as any) || 0;
      return sum + installment;
    }, 0);
    return { installment: Number(totalInstallment) };
  }, [existingAssets]);

  // Calculate totals for planning assets
  const planningTotals = useMemo(() => {
    const totalInstallment = planningAssets.reduce((sum, asset) => {
      const installment = typeof asset.installment === 'number' ? asset.installment : parseFloat(asset.installment as any) || 0;
      return sum + installment;
    }, 0);
    return { installment: Number(totalInstallment) };
  }, [planningAssets]);

  // In create mode (!isEdit), always allow editing. In edit mode, use isEditing state.
  const canEdit = !isEdit || isEditing;

  // Format model/address based on type
  const getModelAddress = (asset: CreateCustomerAssetData) => {
    if (asset.type === 'motor_vehicle') {
      return asset.model || '';
    } else {
      return asset.address || '';
    }
  };

  // Process assets with change requests (similar to AddressTab)
  const processAssets = (category: 'existing' | 'planning') => {
    const assets = category === 'existing' ? existingAssets : planningAssets;
    const deletedAssetsForCategory = deletedAssets.filter(d => d.category === category);
    
    // Create a set to track which assets (by id) are deleted for more accurate matching
    const deletedAssetIds = new Set<number>();
    deletedAssetsForCategory.forEach(deleted => {
      if (deleted.data?.id) {
        deletedAssetIds.add(deleted.data.id);
      }
    });
    
    // Start with all current assets, calculating their original indices correctly
    const allAssets: Array<any> = [];
    
    // First, add all current assets with their calculated original indices
    assets.forEach((asset, currentIdx) => {
      const assetId = (asset as any).id;
      // Skip if this asset is marked for deletion
      if (!assetId || !deletedAssetIds.has(assetId)) {
        // Calculate original index: current index + number of deleted items before this position
        let originalIdx = currentIdx;
        deletedAssetsForCategory.forEach(deleted => {
          if (deleted.index <= currentIdx) {
            originalIdx++;
          }
        });
        // Save both original index (_crIndex) and actual index in assets array (_actualIndex)
        allAssets.push({ ...asset, _crIndex: originalIdx, _actualIndex: currentIdx, category });
      }
    });

    // Sort by _crIndex to ensure correct order before inserting deleted items
    allAssets.sort((a, b) => a._crIndex - b._crIndex);

    // Add deleted assets back at their original positions using splice
    // Process deleted items in reverse order of index to avoid index shifting issues
    const sortedDeleted = [...deletedAssetsForCategory].sort((a, b) => b.index - a.index);
    sortedDeleted.forEach(deleted => {
      if (deleted.data && deleted.data.id) {
        // Check if this asset is already in allAssets
        const exists = allAssets.some(a => {
          const aId = (a as any).id;
          return aId && aId === deleted.data.id;
        });
        if (!exists) {
          // Insert at the original index position using splice
          allAssets.splice(deleted.index, 0, { 
            ...deleted.data, 
            _crIndex: deleted.index, 
            _isDeletedLocal: true,
            category,
          });
        }
      }
    });

    // Add new assets from change requests (pending add requests)
    // Handle asset_{category}_{index}_add format: each request contains a single asset object
    let addRequestIndex = 0;
    changeRequests.forEach(cr => {
      if (cr.status === 'pending' && cr.field_name.endsWith('_add')) {
        const parts = cr.field_name.split('_');
        if (parts.length >= 3 && parts[0] === 'asset' && parts[1] === category) {
          const index = parseInt(parts[2], 10);
          try {
            const newAsset = JSON.parse(cr.new_value || '{}');
            
            // Skip if empty (at least type should be present)
            if (!newAsset.type) {
              return;
            }
            
            // Check if this asset already exists in allAssets
            const existsInAllAssets = allAssets.some(a => {
              const aId = (a as any).id;
              const newAssetId = newAsset.id || '';
              if (aId && newAssetId && aId === newAssetId) {
                return true;
              }
              return false;
            });
            
            // Only add if it doesn't exist in allAssets (to avoid duplicates)
            if (!existsInAllAssets) {
              // Use a high index to ensure these appear at the end
              const placeholder = { 
                ...newAsset, 
                _isNewFromRequest: true, 
                _addRequest: cr, 
                _crIndex: 999 + addRequestIndex,
                category,
              };
              // Always append add-request rows to the end
              allAssets.push(placeholder as any);
              addRequestIndex++;
            }
          } catch (e) {
            console.error('AssetsTab: Error parsing asset_add request:', e);
          }
        }
      }
    });

    const assetData = allAssets.map((asset, idx) => {
      const crIdx = asset._crIndex !== undefined ? asset._crIndex : idx;
      // Check if this specific asset (by id) is marked for deletion
      const assetId = (asset as any).id;
      const isDeleted = asset._isDeletedLocal || 
                       (assetId && deletedAssetsForCategory.some(d => {
                         if (d.index === crIdx && d.data) {
                           return d.data.id === assetId;
                         }
                         return false;
                       })) ||
                       changeRequests.some(cr => 
                         cr.field_name === `asset_${category}_${crIdx}_delete` && cr.status === 'pending'
                       );
      const deleteReq = changeRequests.find(cr => 
        cr.field_name === `asset_${category}_${crIdx}_delete` && cr.status === 'pending'
      );
      return {
        ...asset,
        key: `asset-${category}-${crIdx}-${idx}-${assetId || 'new'}-${asset._isNewFromRequest ? 'new-request' : ''}`,
        originalIndex: crIdx,
        actualIndex: asset._actualIndex !== undefined ? asset._actualIndex : undefined,
        isDeleted: isDeleted,
        deleteRequest: deleteReq,
        isNewFromRequest: asset._isNewFromRequest || false,
        addRequest: asset._addRequest,
      };
    });

    // Sort assets by originalIndex to maintain original order
    // But also ensure: deleted < existing < pending add from request < local new row
    const getRowWeight = (record: any) => {
      if (record.isDeleted) return 0;
      if (record.isNewFromRequest) return 2; // pending add from backend
      if (!record.id) return 3; // local new row (not yet requested)
      return 1; // existing row
    };

    assetData.sort((a: any, b: any) => {
      const wA = getRowWeight(a);
      const wB = getRowWeight(b);
      if (wA !== wB) return wA - wB;
      const idxA = a.originalIndex;
      const idxB = b.originalIndex;
      return idxA - idxB;
    });

    return assetData;
  };

  // Process assets for each category
  const processedExistingAssets = useMemo(() => processAssets('existing'), [existingAssets, deletedAssets, changeRequests]);
  const processedPlanningAssets = useMemo(() => processAssets('planning'), [planningAssets, deletedAssets, changeRequests]);

  // Render asset table for a category
  const renderAssetTable = (category: 'existing' | 'planning', assets: CreateCustomerAssetData[], totals: { installment: number }) => {
    const isExisting = category === 'existing';
    const processedAssets = category === 'existing' ? processedExistingAssets : processedPlanningAssets;

    // Get change request for a specific field
    const getFieldChangeRequest = (index: number, field: 'plate_no' | 'model' | 'address' | 'year' | 'price' | 'installment' | 'type', assetId?: number) => {
      return changeRequests.find(cr => {
        if (cr.status !== 'pending') return false;
        // Match pattern: asset_{category}_{index}_{field}
        const pattern = `asset_${category}_${index}_${field}`;
        return cr.field_name === pattern;
      });
    };

    // Get delete change requests
    const getDeleteChangeRequest = (index: number) => {
      return changeRequests.find(cr => 
        cr.field_name === `asset_${category}_${index}_delete` && cr.status === 'pending'
      );
    };

    const hasPendingFieldChange = (index: number) => {
      return changeRequests.some(
        (cr) =>
          cr.status === 'pending' &&
          cr.field_name.startsWith(`asset_${category}_${index}_`) &&
          !cr.field_name.endsWith('_add') &&
          !cr.field_name.endsWith('_delete')
      );
    };

    const addAsset = isExisting ? addExistingAsset : addPlanningAsset;
    const removeAsset = isExisting ? removeExistingAsset : removePlanningAsset;
    const updateAsset = isExisting ? updateExistingAsset : updatePlanningAsset;

    const normalizeDateValue = (value?: string | number | null): string => {
      if (value === null || value === undefined) return '';
      if (typeof value === 'number') return `${value}-01-01`;
      const trimmed = value.trim();
      if (/^\d{4}$/.test(trimmed)) return `${trimmed}-01-01`;
      return trimmed;
    };

    const toDayjsDate = (value?: string | number | null) => {
      const normalized = normalizeDateValue(value);
      if (!normalized) return null;
      const parsed = dayjs(normalized);
      return parsed.isValid() ? parsed : null;
    };

    const formatDisplayDate = (value?: string | number | null) => {
      const parsed = toDayjsDate(value);
      return parsed ? parsed.format('DD/MM/YYYY') : '-';
    };

    const renderDeletedValue = (content: React.ReactNode) => (
      <span style={{ textDecoration: 'line-through', color: '#999' }}>{content}</span>
      );

    const columns = [
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: 120,
        render: (type: string, record: any) => {
          const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
          const typeChangeRequest = getFieldChangeRequest(record.originalIndex, 'type', record.id);
          
          if (canEdit && !record.isDeleted) {
            let displayValue = type || '';
            // Priority: pending change > change request > add request > record value
            if (getAssetDisplayValue) {
              // Use actualIndex if available (for items in assets array), otherwise use originalIndex
              const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
              const pending = getAssetDisplayValue(category, indexToUse, 'type');
              if (pending !== '') {
                displayValue = pending;
              }
            }
            if (typeChangeRequest && typeChangeRequest.new_value) {
              displayValue = typeChangeRequest.new_value;
            } else if (record.isNewFromRequest && record.addRequest) {
              try {
                const newAsset = JSON.parse(record.addRequest.new_value || '{}');
                displayValue = newAsset.type || type || '';
              } catch (e) {
                displayValue = type || '';
              }
            }
            return (
              <Select
                value={displayValue}
                placeholder="Type"
                style={{ width: '100%' }}
                onChange={(val) => {
                  // Use actualIndex if available (for items in assets array), otherwise use originalIndex
                  const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
                  updateAsset(indexToUse, 'type', val);
                  handleAssetFieldChange(category, indexToUse, 'type', val);
                }}
                options={[
                  { label: 'Motor Vehicle', value: 'motor_vehicle' },
                  { label: 'Property', value: 'property' },
                ]}
              />
            );
          }

          if (typeChangeRequest) {
            if (typeChangeRequest.status === 'approved') {
              return <span>{typeChangeRequest.new_value === 'motor_vehicle' ? 'Motor Vehicle' : typeChangeRequest.new_value === 'property' ? 'Property' : typeChangeRequest.new_value || type || '-'}</span>;
            }
            if (typeChangeRequest.status === 'rejected') {
              return <span>{typeChangeRequest.old_value === 'motor_vehicle' ? 'Motor Vehicle' : typeChangeRequest.old_value === 'property' ? 'Property' : typeChangeRequest.old_value || type || '-'}</span>;
            }
            return (
              <div>
                <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
                  {typeChangeRequest.old_value === 'motor_vehicle' ? 'Motor Vehicle' : typeChangeRequest.old_value === 'property' ? 'Property' : typeChangeRequest.old_value || type || '-'}
                </span>
                <span style={{ color: '#ff9800' }}>
                  {typeChangeRequest.new_value === 'motor_vehicle' ? 'Motor Vehicle' : typeChangeRequest.new_value === 'property' ? 'Property' : typeChangeRequest.new_value || '-'}
                </span>
              </div>
            );
          }

          const typeLabel = type === 'motor_vehicle' ? 'Motor Vehicle' : type === 'property' ? 'Property' : '-';
          return <span style={cellStyle}>{typeLabel}</span>;
        },
      },
      {
        title: 'Model/Address',
        key: 'model_address',
        width: 200,
        render: (_: any, record: any) => {
          const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
          const field = record.type === 'motor_vehicle' ? 'model' : 'address';
          const modelChangeRequest = getFieldChangeRequest(record.originalIndex, 'model', record.id);
          const addressChangeRequest = getFieldChangeRequest(record.originalIndex, 'address', record.id);
          const fieldChangeRequest = field === 'model' ? modelChangeRequest : addressChangeRequest;
          
          if (canEdit && !record.isDeleted) {
            let displayValue = record[field] || '';
            // Priority: pending change > change request > add request > record value
            if (getAssetDisplayValue) {
              // Use actualIndex if available (for items in assets array), otherwise use originalIndex
              const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
              const pending = getAssetDisplayValue(category, indexToUse, field);
              if (pending !== '') {
                displayValue = pending;
              }
            }
            if (fieldChangeRequest && fieldChangeRequest.new_value) {
              displayValue = fieldChangeRequest.new_value;
            } else if (record.isNewFromRequest && record.addRequest) {
              try {
                const newAsset = JSON.parse(record.addRequest.new_value || '{}');
                displayValue = newAsset[field] || record[field] || '';
              } catch (e) {
                displayValue = record[field] || '';
              }
            }
            return (
              <Input
                value={displayValue}
                placeholder={record.type === 'motor_vehicle' ? 'Model' : 'Address'}
                onChange={(e) => {
                  const val = e.target.value;
                  // Use actualIndex if available (for items in assets array), otherwise use originalIndex
                  const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
                  updateAsset(indexToUse, field, val);
                  handleAssetFieldChange(category, indexToUse, field, val);
                }}
              />
            );
          }

          if (fieldChangeRequest) {
            if (fieldChangeRequest.status === 'approved') {
              return <span>{fieldChangeRequest.new_value || getModelAddress(record) || '-'}</span>;
            }
            if (fieldChangeRequest.status === 'rejected') {
              return <span>{fieldChangeRequest.old_value || getModelAddress(record) || '-'}</span>;
            }
            return (
              <div>
                <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
                  {fieldChangeRequest.old_value || getModelAddress(record) || '-'}
                </span>
                <span style={{ color: '#ff9800' }}>
                  {fieldChangeRequest.new_value || '-'}
                </span>
              </div>
            );
          }

          return <span style={cellStyle}>{getModelAddress(record) || '-'}</span>;
        },
      },
      {
        title: 'Date',
        dataIndex: 'year',
        key: 'year',
        width: 140,
        render: (year: string | number | undefined, record: any) => {
          const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
          const yearChangeRequest = getFieldChangeRequest(record.originalIndex, 'year', record.id);
          
          if (canEdit && !record.isDeleted) {
            let displayDate = year;
            if (yearChangeRequest && yearChangeRequest.new_value) {
              displayDate = yearChangeRequest.new_value;
            } else if (getAssetDisplayValue) {
              // Use actualIndex if available (for items in assets array), otherwise use originalIndex
              const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
              const pending = getAssetDisplayValue(category, indexToUse, 'year');
              if (pending !== '') {
                displayDate = pending;
              }
            }
            return (
              <DatePicker
                value={toDayjsDate(displayDate)}
                onChange={(date) => {
                  const val = date ? date.format('YYYY-MM-DD') : undefined;
                  // Use actualIndex if available (for items in assets array), otherwise use originalIndex
                  const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
                  updateAsset(indexToUse, 'year', val);
                  handleAssetFieldChange(category, indexToUse, 'year', val ?? '');
                }}
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
              />
            );
          }

          const displayYear = yearChangeRequest?.new_value ? yearChangeRequest.new_value : year;
          const display = formatDisplayDate(displayYear);
          
          if (yearChangeRequest) {
            if (yearChangeRequest.status === 'approved') {
              return <span>{display}</span>;
            }
            if (yearChangeRequest.status === 'rejected') {
              const oldYear = yearChangeRequest.old_value ? yearChangeRequest.old_value : year;
              const oldDisplay = formatDisplayDate(oldYear);
              return <span>{oldDisplay}</span>;
            }
            const oldYear = yearChangeRequest.old_value ? yearChangeRequest.old_value : year;
            const oldDisplay = formatDisplayDate(oldYear);
            return (
              <div>
                <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
                  {oldDisplay}
                </span>
                <span style={{ color: '#ff9800' }}>{display}</span>
              </div>
            );
          }

          return <span style={cellStyle}>{display}</span>;
        },
      },
      {
        title: 'Instalment',
        dataIndex: 'installment',
        key: 'installment',
        width: 150,
        render: (installment: number | string | undefined, record: any) => {
          const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
          const installmentChangeRequest = getFieldChangeRequest(record.originalIndex, 'installment', record.id);
          
          if (canEdit && !record.isDeleted) {
            let displayVal = installment;
            if (installmentChangeRequest && installmentChangeRequest.new_value) {
              displayVal = parseFloat(installmentChangeRequest.new_value);
            } else if (getAssetDisplayValue) {
              const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
              const pending = getAssetDisplayValue(category, indexToUse, 'installment');
              if (pending !== '') {
                displayVal = parseFloat(pending);
              }
            }
            // Keep existing installment value if available
            return (
              <Input
                type="number"
                value={displayVal || ''}
                onChange={(e) => {
                  const val = e.target.value ? parseFloat(e.target.value) : undefined;
                  const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
                  updateAsset(indexToUse, 'installment', val);
                  handleAssetFieldChange(category, indexToUse, 'installment', e.target.value);
                }}
                placeholder="0.00"
              />
            );
          }

          const displayVal = installmentChangeRequest?.new_value ? parseFloat(installmentChangeRequest.new_value) : installment;
          const numInstallment = displayVal ? (typeof displayVal === 'number' ? displayVal : parseFloat(displayVal as string) || 0) : null;
          const formatted = numInstallment !== null ? numInstallment.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '-';
          
          if (installmentChangeRequest) {
            if (installmentChangeRequest.status === 'approved') {
              return <span>{formatted}</span>;
            }
            if (installmentChangeRequest.status === 'rejected') {
              const oldNum = installmentChangeRequest.old_value ? parseFloat(installmentChangeRequest.old_value) : null;
              const oldFormatted = oldNum !== null ? oldNum.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '-';
              return <span>{oldFormatted}</span>;
            }
            const oldNum = installmentChangeRequest.old_value ? parseFloat(installmentChangeRequest.old_value) : null;
            const oldFormatted = oldNum !== null ? oldNum.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '-';
            return (
              <div>
                <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
                  {oldFormatted}
                </span>
                <span style={{ color: '#ff9800' }}>{formatted}</span>
              </div>
            );
          }

          return <span style={cellStyle}>{formatted}</span>;
        },
      },
    ];

    columns.push({
      title: 'Action',
      key: 'action',
      width: 200,
      render: (_: any, record: any) => {
        // Handle delete request
        if (record.isDeleted && record.deleteRequest) {
          if (!isEditing) return <span></span>;
          if (isCustomerPortal) {
            return <Tag color="orange">Pending</Tag>;
          } else {
            return <span></span>;
          }
        }

        // Handle field changes from change requests
        const typeChangeRequest = getFieldChangeRequest(record.originalIndex, 'type', record.id);
        const plateNoChangeRequest = getFieldChangeRequest(record.originalIndex, 'plate_no', record.id);
        const modelChangeRequest = getFieldChangeRequest(record.originalIndex, 'model', record.id);
        const addressChangeRequest = getFieldChangeRequest(record.originalIndex, 'address', record.id);
        const yearChangeRequest = getFieldChangeRequest(record.originalIndex, 'year', record.id);
        const installmentChangeRequest = getFieldChangeRequest(record.originalIndex, 'installment', record.id);
        
        const hasAnyChangeRequest = typeChangeRequest || plateNoChangeRequest || modelChangeRequest || 
                                   addressChangeRequest || yearChangeRequest || installmentChangeRequest;
        
        if (hasAnyChangeRequest && !record.isDeleted) {
            const hasPending = [typeChangeRequest, plateNoChangeRequest, modelChangeRequest, addressChangeRequest, 
                              yearChangeRequest, installmentChangeRequest].some(cr => cr?.status === 'pending');
          
          if (!isEditing) return <span></span>;
          
          if (isCustomerPortal) {
            return hasPending ? <Tag color="orange">Pending</Tag> : <span></span>;
          }
          
          return <span></span>;
        }

        // Handle new asset from change request (add request)
        if (record.isNewFromRequest && record.addRequest) {
          if (!isEditing) return <span></span>;
          // Customer portal: show pending status
          if (isCustomerPortal) {
            return <Tag color="orange">Pending</Tag>;
          }
          return <span></span>;
        }

        // Delete button: show for existing rows (with id) and new local rows (without id and not from request)
        if (canEdit && !record.isDeleted && !record.isNewFromRequest) {
          return (
            <Button
              danger
              size="small"
              onClick={() => {
                // Use actualIndex if available (for items in assets array), otherwise use originalIndex
                const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
                removeAsset(indexToUse);
              }}
            >
              Delete
            </Button>
          );
        }

        return <span></span>;
      },
    });

    return (
      <div>
        <Table
          columns={columns}
          dataSource={processedAssets}
          pagination={false}
          size="small"
          bordered
          onRow={(record: any) => {
            const isDeleted = record.isDeleted;
            const isNew = record.isNewFromRequest;
            const isChanged = hasPendingFieldChange(record.originalIndex);

            // Priority: delete (red) > add (blue) > change (yellow)
            if (isDeleted) {
              return { style: { backgroundColor: '#fff1f0' } };
            }
            if (isNew) {
              return { style: { backgroundColor: '#e6f7ff' } };
            }
            if (isChanged) {
              return { style: { backgroundColor: '#fffbe6' } };
            }
            return {};
          }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <strong>Total</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3}>
                  <strong>{totals.installment.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4}></Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {canEdit && processedAssets.filter(a => !a.isDeleted && !a.isNewFromRequest).length < maxAssets && (
            <Button
              type="link"
              icon={<PlusOutlined />}
              onClick={addAsset}
              style={{ color: '#ff4d4f', padding: 0 }}
            >
              (max {maxAssets})
            </Button>
          )}
          {!canEdit && processedAssets.length === 0 && (
            <span style={{ color: '#999' }}>No assets</span>
          )}
        </div>
      </div>
    );
  };

  const renderCategoryButtons = (
    category: 'existing' | 'planning',
    onChange: (val: 'existing' | 'planning') => void
  ) => {
    const baseStyle = {
      borderRadius: 6,
      padding: '4px 12px',
    } as const;
    const activeStyle = {
      ...baseStyle,
      backgroundColor: '#1890ff',
      borderColor: '#1890ff',
      color: '#fff',
    };
    const inactiveStyle = {
      ...baseStyle,
      backgroundColor: '#fff',
      borderColor: '#d9d9d9',
      color: '#595959',
    };

    return (
      <Space>
        <Button
          style={category === 'existing' ? activeStyle : inactiveStyle}
          onClick={() => onChange('existing')}
        >
          Current
        </Button>
        <Button
          style={category === 'planning' ? activeStyle : inactiveStyle}
          onClick={() => onChange('planning')}
        >
          Planning
        </Button>
      </Space>
    );
  };

  const renderSection = (category: 'existing' | 'planning', onChange: (val: 'existing' | 'planning') => void) => {
    const totals = category === 'existing' ? existingTotals : planningTotals;
    const assets = category === 'existing' ? existingAssets : planningAssets;
    return (
      <div style={{ marginBottom: 24 }}>
        {renderCategoryButtons(category, onChange)}
        {renderAssetTable(category, assets, totals)}
      </div>
    );
  };

  return (
    <div>
      {renderSection(category, setCategory)}
    </div>
  );
};
